/* Climate Flow fault finder: equipment, then symptom, then likely causes, urgency and next step. */
const URGENCY = {
  emergency: { label: 'Emergency: act now', summary: 'This is a safety emergency. Follow these steps before anything else.' },
  urgent: { label: 'Urgent: call now', summary: 'The longer this runs, the more stock or comfort is at risk. Call and we will prioritise it.' },
  soon: { label: 'Book a visit soon', summary: 'Not an emergency yet, but get it looked at before it gets worse.' },
  routine: { label: 'Routine', summary: 'Book it in with your next service visit.' },
};

const escapeHtml = (value) =>
  String(value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

class CfFaultFinder extends HTMLElement {
  connectedCallback() {
    const raw = this.querySelector('script[type="application/json"]');
    if (!raw) return;
    try {
      this.data = JSON.parse(raw.textContent);
    } catch (error) {
      return;
    }
    this.equipmentList = this.querySelector('[data-ff-equipment]');
    this.symptomStep = this.querySelector('[data-ff-symptom-step]');
    this.symptomList = this.querySelector('[data-ff-symptoms]');
    this.result = this.querySelector('[data-ff-result]');
    this.placeholder = this.querySelector('[data-ff-placeholder]');
    this.quoteUrl = this.dataset.quoteUrl || '/pages/contact';
    this.renderEquipment();
  }

  chip(label, group, onSelect) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'cf-chip';
    button.textContent = label;
    button.setAttribute('aria-pressed', 'false');
    button.addEventListener('click', () => {
      group.querySelectorAll('.cf-chip').forEach((chip) => chip.setAttribute('aria-pressed', 'false'));
      button.setAttribute('aria-pressed', 'true');
      onSelect();
    });
    return button;
  }

  renderEquipment() {
    this.equipmentList.replaceChildren(
      ...this.data.equipment.map((item) => this.chip(item.label, this.equipmentList, () => this.selectEquipment(item)))
    );
  }

  selectEquipment(item) {
    this.symptomList.replaceChildren(
      ...item.symptoms.map((symptom) => this.chip(symptom.label, this.symptomList, () => this.showResult(item, symptom)))
    );
    this.symptomStep.hidden = false;
    this.result.hidden = true;
    if (this.placeholder) this.placeholder.hidden = false;
    this.symptomStep.querySelector('[tabindex="-1"]')?.focus({ preventScroll: true });
  }

  showResult(item, symptom) {
    const urgency = URGENCY[symptom.urgency] || URGENCY.soon;
    const phone = this.dataset.phone;
    const phoneDisplay = this.dataset.phoneDisplay;
    const quote = `${this.quoteUrl}?service=${encodeURIComponent(item.service)}&issue=${encodeURIComponent(item.label + ': ' + symptom.label)}#quote`;
    const list = (items) => `<ul>${items.map((text) => `<li>${escapeHtml(text)}</li>`).join('')}</ul>`;

    let actions;
    if (symptom.urgency === 'emergency') {
      actions = `<a class="cf-btn cf-btn--primary" href="tel:0800111999">Call 0800 111 999</a>`;
    } else if (symptom.urgency === 'routine') {
      actions = `<a class="cf-btn cf-btn--primary" href="${quote}">Book a visit</a><a class="cf-btn cf-btn--ghost" href="tel:${phone}">Call ${escapeHtml(phoneDisplay)}</a>`;
    } else {
      actions = `<a class="cf-btn cf-btn--primary" href="tel:${phone}">Call ${escapeHtml(phoneDisplay)}</a><a class="cf-btn cf-btn--ghost" href="${quote}">Request a visit</a>`;
    }

    this.result.dataset.urgency = symptom.urgency;
    this.result.innerHTML = `
      <p class="cf-ff__badge">${symptom.urgency === 'urgent' || symptom.urgency === 'emergency' ? '<span class="cf-led" aria-hidden="true"></span>' : ''}${escapeHtml(urgency.label)}</p>
      <h3 class="cf-ff__title" tabindex="-1">${escapeHtml(item.label)}: ${escapeHtml(symptom.label.toLowerCase())}</h3>
      <p class="cf-ff__summary">${escapeHtml(urgency.summary)}</p>
      ${symptom.causes.length ? `<h4>Likely causes</h4>${list(symptom.causes)}` : ''}
      ${symptom.tips.length ? `<h4>${symptom.urgency === 'emergency' ? 'Do this now' : 'What you can do now'}</h4>${list(symptom.tips)}` : ''}
      <div class="cf-actions">${actions}</div>
      <p class="cf-ff__note">A guide only. An engineer confirms the cause on site.</p>`;
    this.result.hidden = false;
    if (this.placeholder) this.placeholder.hidden = true;

    const heading = this.result.querySelector('.cf-ff__title');
    heading?.focus({ preventScroll: true });
    if (window.matchMedia('(max-width: 989px)').matches) {
      this.result.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'cf_fault_result', cf_equipment: item.id, cf_symptom: symptom.label, cf_urgency: symptom.urgency });
  }
}

if (!customElements.get('cf-fault-finder')) customElements.define('cf-fault-finder', CfFaultFinder);
